import { FC } from 'react';
import { IonButton, IonSpinner } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from './RecordingState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { DuoClipboardListIcon, DuoHandDotsIcon, DuoPrescriptionBottleIcon, MicrophoneIcon } from '../theme/Icons';
import { Card } from '../components/Card';

export interface IRecordingContext {
    type?: 'at-appointment' | 'post-appointment';
    label: string;
    consentHeader: string;
    consentMessage: string;
}

const CATEGORY_PILLS = [
    { label: 'Diagnoses', Icon: DuoClipboardListIcon },
    { label: 'Medications', Icon: DuoPrescriptionBottleIcon },
    { label: 'Allergies', Icon: DuoHandDotsIcon }
];

const useStyles = createUseStyles({
    container: {
    },
    cardContent: {
        padding: '24px 24px 28px'
    },
    beaHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16
    },
    beaAvatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        objectFit: 'cover'
    },
    beaName: {
        fontSize: 16,
        fontWeight: 600,
        color: 'var(--ion-color-dark)'
    },
    body: {
        fontSize: 15,
        lineHeight: 1.5,
        color: '#555',
        marginBottom: 20
    },
    pillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 24
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        fontWeight: 500,
        padding: '5px 12px',
        borderRadius: 20,
        color: 'var(--ion-color-primary)',
        backgroundColor: 'rgba(var(--ion-color-primary-rgb), 0.1)',
        border: '1px solid rgba(var(--ion-color-primary-rgb), 0.25)'
    },
    recordButton: {
        fontWeight: '600',
        fontSize: '16px',
        height: '52px'
    },
    testimonial: {
        textAlign: 'center',
        padding: '16px 24px 20px'
    },
    quote: {
        fontSize: 15,
        lineHeight: 1.6,
        fontStyle: 'italic',
        color: '#888'
    },
    attribution: {
        fontSize: 14,
        color: '#aaa',
        marginTop: 12
    },
    heading: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 12,
        color: 'var(--ion-color-dark)',
        textAlign: 'center'
    },
    viewerMessage: {
        textAlign: 'center',
        margin: 'auto',
        marginBottom: 32,
        maxWidth: 300
    },
    addLink: {
        display: 'inline-block',
        cursor: 'pointer',
        color: 'var(--ion-color-primary)'
    }
});

export const RecordingEmptyState: FC = () => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);
    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);
    const careMap = careMapState.getMostRecentCaremap();
    const isViewer = careMap && !careMap?.links?.self?.actions.includes('put');

    const addRecipient = () => {
        history.push('/q/new-care-recipient');
    };

    const handleRecord = () => {
        recordingState.triggerRecording({ initiatedFrom: 'Intro Card', summaryPrompt: 'intro' });
    };

    if (isViewer) {
        return (
            <div className={classes.container}>
                <div className={classes.heading}>
                    You don't have to do this alone
                </div>
                <div className={classes.viewerMessage}>
                    You do not have permission to add recordings for {careMapState.getPatientFullName()}.&nbsp;
                    <div className={classes.addLink} onClick={addRecipient}>
                        Add
                    </div> or switch to another care recipient to start recording.
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Card>
                <div className={classes.cardContent}>
                <div className={classes.beaHeader}>
                    <img
                        src="/assets/images/help/helper-bea.png"
                        alt="Bea"
                        className={classes.beaAvatar}
                    />
                    <span className={classes.beaName}>Bea</span>
                </div>
                <div className={classes.body}>
                    Tell me about who you're caring for and I'll set up their profile for you!
                </div>
                <div className={classes.pillsRow}>
                    {CATEGORY_PILLS.map(({ label, Icon }) => (
                        <span key={label} className={classes.pill}>
                            <Icon color="primary" customSize={14} />
                            {label}
                        </span>
                    ))}
                </div>
                <IonButton
                    expand="block"
                    color="primary"
                    className={classes.recordButton}
                    disabled={recordingState.isStartingRecording()}
                    onClick={handleRecord}
                    data-id="intro-record-button"
                >
                    {recordingState.isStartingRecording() ? (
                        <IonSpinner name="dots" style={{height: 20}} />
                    ) : (
                        <>
                            <MicrophoneIcon color="light" customSize={20} />
                            <span style={{marginLeft: 8}}>Record</span>
                        </>
                    )}
                </IonButton>
                </div>
            </Card>

            <div className={classes.testimonial}>
                <div className={classes.quote}>
                    &ldquo;I just started talking about my mom and
                    in two minutes, Bea had it all captured.
                    I didn't even realize how much
                    I was carrying around in my head.&rdquo;
                </div>
                <div className={classes.attribution}>
                    &mdash; Sarah, caring for her mother
                </div>
            </div>
        </div>
    );
};
